import { type MouseEvent, memo, type ReactNode, useCallback, useState } from 'react';

import { Popover, type PopoverProps } from '@air/primitive-popover';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { FilterButton, type FilterButtonProps } from '~/components/Filters/SingleFilters/FilterButton';
import { DropdownMenuActionSheet } from '~/components/Menus/DropdownMenuWithActionSheet';

export type FilterTriggerProps = Omit<FilterButtonProps, 'isActive'> &
  Pick<PopoverProps, 'onOpenChange'> & {
    filterContent: ReactNode;
    popoverClassName?: string;
    values?: string[];
  };

export const FilterTrigger = memo(
  ({
    count,
    title,
    onRemoveClick,
    filterContent,
    Icon,
    popoverClassName,
    values,
    onOpenChange: _onOpenChange,
  }: FilterTriggerProps) => {
    const [showActionSheet] = useAirModal(DropdownMenuActionSheet);
    const { isAboveMediumScreen } = useBreakpointsContext();
    const [isExpanded, setIsExpanded] = useState(false);

    const onOpenChange = useCallback(
      (open: boolean) => {
        setIsExpanded(open);
        _onOpenChange?.(open);
      },
      [_onOpenChange],
    );
    const handleMobileOpen = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        showActionSheet({
          content: filterContent,
          onChange: setIsExpanded,
          title,
        });
      },
      [filterContent, showActionSheet, title],
    );

    if (isAboveMediumScreen) {
      return (
        <Popover
          className={cn('w-auto p-3 md:w-80', popoverClassName)}
          onOpenChange={onOpenChange}
          trigger={
            <FilterButton
              count={count}
              Icon={Icon}
              isOpen={isExpanded}
              onRemoveClick={onRemoveClick}
              title={title}
              values={values}
            />
          }
        >
          {filterContent}
        </Popover>
      );
    }

    return (
      <FilterButton
        count={count}
        Icon={Icon}
        isOpen={isExpanded}
        onRemoveClick={onRemoveClick}
        onClick={handleMobileOpen}
        title={title}
        values={values}
      />
    );
  },
);

FilterTrigger.displayName = 'FilterTrigger';
